/**
 * The hiring screen. Two questions, and then you are on the floor.
 *
 * It sits above the click-to-play veil rather than inside it, because the veil
 * is a thing you dismiss and this is a thing you answer. It is the first screen
 * of a new week and the only screen in the game that the player cannot walk
 * past — which is the same contract the chapter gate has, and it is built the
 * same way: no close button, no Escape, one button forward.
 *
 * What it deliberately does not have: a stat, a background, a starting perk, a
 * department you are "from". You are a new hire in your first week and you
 * start at nothing with all seven of them, and the screen says so out loud,
 * because the alternative is a player spending three minutes looking for the
 * choice that matters on a screen where none of them do.
 */
import { BODIES } from '../data/bodies.js';
import { NAME_LIMIT, cleanName } from '../sim/identity.js';

export function createCreation({ parent, bodies = BODIES, onHire }) {
  const root = document.createElement('div');
  root.className = 'hire';
  root.hidden = true;
  parent.append(root);

  let bodyId = bodies[0].id;

  root.innerHTML = `
    <div class="hire-card">
      <span class="hire-kicker">New starter</span>
      <h2>The Lion &amp; Crown</h2>
      <p class="hire-blurb">Banquets. First week. Nobody here knows you yet, and
        everybody here is going to have an opinion.</p>
      <label class="hire-field">
        <span>Name on the badge</span>
        <input class="hire-name" type="text" maxlength="${NAME_LIMIT}" autocomplete="off"
          spellcheck="false" placeholder="Type a name">
      </label>
      <button class="hire-random" type="button" data-pad-default>Pick a name for me</button>
      <span class="hire-label">Pick a look</span>
      <div class="hire-bodies">${bodies
        .map(
          (body) => `<button class="hire-body" type="button" data-body="${body.id}"
              data-on="${body.id === bodyId ? 'yes' : 'no'}" title="${body.label}">
              <i style="background:#${body.blacks.toString(16).padStart(6, '0')}"></i>
              <i style="background:#${body.civvies.toString(16).padStart(6, '0')}"></i>
              <span>${body.label}</span>
            </button>`
        )
        .join('')}</div>
      <p class="hire-note">Blacks are your uniform. The other one is what you
        turned up in. Neither of them buys you anything: the look is a look.</p>
      <button class="hire-start" type="button" disabled>Start the week</button>
    </div>`;

  const field = root.querySelector('.hire-name');
  const start = root.querySelector('.hire-start');

  function refresh() {
    start.disabled = cleanName(field.value).length === 0;
  }

  field.addEventListener('input', refresh);

  /*
   * A name without a keyboard. A pad has no letters, and the badge needs a
   * name, so this fills one in from a short list; the field can still be typed
   * over. Which one is the clock's business, not a seed's: it is a name, and
   * nothing in the game reads anything into it.
   */
  const NAMES = ['Sam Ortega', 'Jo Whitley', 'Dani Reyes', 'Alex Moreno', 'Kit Bailey', 'Rene Castillo', 'Lou Park', 'Casey Lin'];
  let nameAt = Math.floor(Date.now() / 1000) % NAMES.length;
  root.querySelector('.hire-random').addEventListener('click', () => {
    field.value = NAMES[nameAt % NAMES.length];
    nameAt += 1;
    refresh();
  });
  field.addEventListener('keydown', (event) => {
    if (event.code !== 'Enter' && event.key !== 'Enter') return;
    event.preventDefault();
    hire();
  });

  root.addEventListener('click', (event) => {
    const pick = event.target.closest('.hire-body');
    if (!pick) return;
    bodyId = pick.dataset.body;
    for (const button of root.querySelectorAll('.hire-body')) {
      button.dataset.on = button.dataset.body === bodyId ? 'yes' : 'no';
    }
  });

  function hire() {
    const name = cleanName(field.value);
    if (!name) {
      field.focus();
      return;
    }
    root.hidden = true;
    onHire?.({ name, body: bodyId });
  }

  start.addEventListener('click', hire);

  return {
    root,
    get open() {
      return !root.hidden;
    },
    show({ name = '', body = bodies[0].id } = {}) {
      field.value = name;
      bodyId = body;
      for (const button of root.querySelectorAll('.hire-body')) {
        button.dataset.on = button.dataset.body === bodyId ? 'yes' : 'no';
      }
      refresh();
      root.hidden = false;
      field.focus();
    },
    /**
     * Fills the form and submits it, for the headless scripts.
     *
     * They go through the real screen rather than round it: every script that
     * boots the game is then also a test that a week can be started, which is
     * the one screen a broken build would otherwise hide behind.
     */
    hire({ name, body }) {
      field.value = name ?? '';
      if (body) bodyId = body;
      hire();
    },
  };
}
